import { parseInertBody } from "~/shared/libs/customHtmlRepair.ts";

/** Duplicated in `apps/statamic/app/Rules/CustomHtml/CloudinaryTransformed.php` on purpose — sharing cost more than the drift. */
const cloudinaryHost = "res.cloudinary.com";
const cloudinaryImageResourceType = "image";
const cloudinaryRequiredTokens = ["f_auto", "q_auto"];

/** A prefix carries a value — `w_800`, `w_0.5`, `w_auto:100` — so a public id like `w_logo.png` fails it. */
const cloudinaryPrefixPatterns = [
  /^w_(?:\d+(?:\.\d+)?|auto(?::[\w:]+)?)$/,
  /^h_(?:\d+(?:\.\d+)?|auto(?::[\w:]+)?)$/,
];

/** Cloudinary resolves a named transformation server-side, so it may already carry every requirement. */
const cloudinaryNamedTransformationPattern = /^t_[\w-]+$/;

/** A base for relative values that can never match the cloudinary host. */
const relativeSrcBase = "https://relative.invalid";

function cloudinaryPath(src: string): string | null {
  if (!URL.canParse(src, relativeSrcBase)) {
    return null;
  }
  const { hostname, pathname } = new URL(src, relativeSrcBase);

  return hostname === cloudinaryHost ? pathname : null;
}

function cloudinaryImageParameters(path: string): string[] | null {
  const segments = path.replace(/^\/+/, "").replace(/\/+$/, "").split("/");
  const uploadedAt = segments.indexOf("upload");
  if (
    uploadedAt < 1 ||
    segments[uploadedAt - 1] !== cloudinaryImageResourceType
  ) {
    return null;
  }
  return segments
    .slice(uploadedAt + 1)
    .flatMap((segment) => segment.split(","));
}

function hasRequiredTransformations(parameters: string[]): boolean {
  if (
    parameters.some((parameter) =>
      cloudinaryNamedTransformationPattern.test(parameter),
    )
  ) {
    return true;
  }
  return (
    cloudinaryRequiredTokens.every((token) => parameters.includes(token)) &&
    cloudinaryPrefixPatterns.every((pattern) => {
      return parameters.some((parameter) => pattern.test(parameter));
    })
  );
}

function isUntransformedCloudinaryUrl(src: string): boolean {
  const path = cloudinaryPath(src);
  if (path === null) {
    return false;
  }
  const parameters = cloudinaryImageParameters(path);

  return parameters !== null && !hasRequiredTransformations(parameters);
}

/** A cloudinary url carries commas of its own, so candidates cannot be split on them: per the srcset grammar a url runs to whitespace, and any descriptor behind it runs to the next comma. */
function srcsetUrls(srcset: string): string[] {
  const urls: string[] = [];
  let cursor = 0;

  while (cursor < srcset.length) {
    const urlStart = srcset.slice(cursor).search(/[^\s,]/);
    if (urlStart === -1) {
      break;
    }
    cursor = cursor + urlStart;

    const urlEnd = srcset.slice(cursor).search(/\s/);
    const url =
      urlEnd === -1
        ? srcset.slice(cursor)
        : srcset.slice(cursor, cursor + urlEnd);
    cursor = urlEnd === -1 ? srcset.length : cursor + urlEnd;
    urls.push(url.replace(/,+$/, ""));

    if (!url.endsWith(",")) {
      const descriptorEnd = srcset.indexOf(",", cursor);
      cursor = descriptorEnd === -1 ? srcset.length : descriptorEnd + 1;
    }
  }

  return urls;
}

/** Both attributes make the browser fetch — a `srcset` candidate is picked without a `src` present — so both are this block's problem. */
function hasUntransformedCloudinaryUrl(html: string): boolean {
  const sources = parseInertBody(html).querySelectorAll("[src], [srcset]");

  return [...sources].some((element) =>
    [
      element.getAttribute("src") ?? "",
      ...srcsetUrls(element.getAttribute("srcset") ?? ""),
    ].some(isUntransformedCloudinaryUrl),
  );
}

export { hasUntransformedCloudinaryUrl };
